import React from 'react';
import { Fade, useTheme } from '@mui/material';
import styled from '@emotion/styled';
import { FileFormat } from '@/constants/constants';
import { useAppSelector } from '@/redux/hooks';
import { selectFileTypes } from '@/redux/selectors/constantsSelectors';
import VideoPlayer from '@/components/_common/VideoPlayer/VideoPlayer';
import MarkdownPreview from '@/components/_common/MarkdownPreview';
import PdfPReview from '@/components/_common/PdfPreview';
import StringPreview, { STRING_PREVIEW_TYPES } from '@/components/_common/StringPreview';

interface Props {
  fileType: FileFormat;
  source: string;
  poster?: string;
  onError?: () => void;
}

const InventoryPreview: React.FC<Props> = ({ fileType, source, poster, onError }) => {
  const fileTypes = useAppSelector(selectFileTypes);
  const isDarkMode = useTheme().palette.mode === 'dark';

  switch (fileTypes[fileType]) {
    case 'video':
      return (
        <VideoPlayer
          key={
            // make sure that the video player is re-rendered when the source changes
            source
          }
          url={source}
          poster={poster}
          autoplay={false}
        />
      );
    case 'audio':
      return (
        <Fade in timeout={600}>
          <AudioPreview isdark={isDarkMode ? 1 : 0}>
            <audio controls src={source || undefined} onError={onError}>
              Your browser does not support the
              <code>audio</code> element.
            </audio>
          </AudioPreview>
        </Fade>
      );
    case 'image':
      return <img src={source || undefined} alt="preview image" width="100%" />;
    default:
      if (fileType === 'md' && !!source) {
        return <MarkdownPreview url={source} />;
      } else if (fileType === 'pdf' && source) {
        return <PdfPReview source={source} />;
      } else if (STRING_PREVIEW_TYPES.includes(fileType) && !!source) {
        return <StringPreview language={fileType} url={source} />;
      } else {
        return <CannotBePreviewed>This file cannot be previewed</CannotBePreviewed>;
      }
  }
};

export default InventoryPreview;

const CannotBePreviewed = styled.div`
  padding: 30px;
  display: flex;
  flex-direction: row;
  font-size: 1.3em;
  justify-content: center;
  align-items: center;
`;

const AudioPreview = styled.div<{ isdark: number }>`
  audio {
    width: 100%;
    filter: ${({ isdark }) => (isdark ? 'invert(1)' : 'none')};
  }
`;
